import OOPFilters from '@/components/DashboardFilters/OOPFilters';
import DashboardLayout from '@/components/layouts/DashboardLayout';
import {
   Table,
   TableBody,
   TableHead,
   TableHeader,
   TableRow,
} from "@/components/ui/table";
import { useMediaQuery } from '@/hooks/useMediaQuery';
import useTabNavigation from '@/hooks/useTabNavigation';
import useTourGuide from '@/hooks/useTourGuide';
import { useTypewriter } from '@/hooks/useTypewriter';
import { gql, useQuery } from '@apollo/client';
import { FileX } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
// import { useLocation, useNavigate } from 'react-router-dom';
import BillCollectorOOPRow from './BC_OOPRow';

const GET_OOPS = gql`
   query GetOOPs {
      getOOPs {
         _id
         billNo
         applicationId
         applicationNumber
         namePayee
         address
         natureOfApplication
         totalAmount
         OOPstatus
         createdAt
         isManualPayment
         isOnlinePayment
         items {
            legalBasis
            description
            amount
         }
         rpsSignatureImage
         tsdSignatureImage
         signatures {
            chiefRPS
            technicalServices
         }
         receivedDate
         receivedTime
         trackingNo
         releasedDate
         releasedTime
         paymentProof {
            transactionId
            paymentMethod
            amount
            timestamp
            referenceNumber
            payerDetails {
               name
               email
               phoneNumber
            }
            status
         }
         officialReceipt {
            orNumber
            dateIssued
            amount
            paymentMethod
            remarks
         }
      }
   }
`;

const BillCollectorDashboard = () => {
   // const navigate = useNavigate();
   // const location = useLocation();

   const [filters, setFilters] = useState({
      searchTerm: '',
      applicationType: '',
      amountRange: '',
      dateRange: {
         from: undefined,
         to: undefined
      }
   });

   const mainTabs = useMemo(() => [
      'Awaiting Payment',
      'Manual Payment',
      // 'Payment Proof',
      'Completed Payments',
      'Issued OR'
   ], []);

   // Initialize tab navigation
   const {
      activeMainTab,
      handleMainTabChange: handleTabChange
   } = useTabNavigation({
      mainTabs,
      defaultMainTab: 'Completed Payments'
   });

   const {
      data: oopsData,
      loading: oopsLoading,
      error: oopsError,
      refetch: refetchOOPs
   } = useQuery(GET_OOPS, {
      fetchPolicy: 'network-only',
   });

   // Define handleRefetch
   const handleRefetch = useCallback(() => {
      refetchOOPs();
   }, [refetchOOPs]);

   useEffect(() => {
      const pollInterval = setInterval(handleRefetch, 30000);
      return () => clearInterval(pollInterval);
   }, [handleRefetch]);

   const [currentPage, setCurrentPage] = useState(1);
   const [itemsPerPage, setItemsPerPage] = useState(10);
   const [totalItems, setTotalItems] = useState(0);

   const filteredOOPs = useMemo(() => {
      if (!oopsData?.getOOPs) return [];

      return oopsData.getOOPs.filter(oop => {
         // Filter based on active tab
         const matchesTab = (() => {
            switch (activeMainTab) {
               case 'Payment Proof':
                  return oop.OOPstatus === 'Payment Proof Submitted';
               case 'Awaiting Payment':
                  return oop.OOPstatus === 'Awaiting Payment' && !oop.isManualPayment;
               case 'Manual Payment':
                  return oop.OOPstatus === 'Awaiting Manual Payment';
               case 'Completed Payments':
                  return oop.OOPstatus === 'Payment Proof Approved' || oop.OOPstatus === 'Completed OOP';
               case 'Issued OR':
                  return oop.OOPstatus === 'Issued OR';
               default:
                  return true;
            }
         })();

         // Search filter
         const matchesSearch = !filters.searchTerm ||
            oop.applicationNumber.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
            oop.billNo.toLowerCase().includes(filters.searchTerm.toLowerCase());

         // Application type filter
         const matchesType = !filters.applicationType ||
            filters.applicationType === "all" ||
            oop.natureOfApplication === filters.applicationType;

         // Amount range filter
         const matchesAmount = !filters.amountRange || (() => {
            const amount = parseFloat(oop.totalAmount);
            switch (filters.amountRange) {
               case '0-1000': return amount >= 0 && amount <= 1000;
               case '1001-5000': return amount > 1000 && amount <= 5000;
               case '5001-10000': return amount > 5000 && amount <= 10000;
               case '10001+': return amount > 10000;
               default: return true;
            }
         })();

         // Date range filter
         const matchesDateRange = (() => {
            if (!filters.dateRange.from && !filters.dateRange.to) return true;
            const oopDate = new Date(parseInt(oop.createdAt));
            if (isNaN(oopDate.getTime())) return true;
            const fromDate = filters.dateRange.from ? new Date(filters.dateRange.from) : null;
            const toDate = filters.dateRange.to ? new Date(filters.dateRange.to) : null;
            return (!fromDate || oopDate >= fromDate) && (!toDate || oopDate <= toDate);
         })();

         return matchesTab && matchesSearch && matchesType && matchesAmount && matchesDateRange;
      });
   }, [oopsData, activeMainTab, filters]);

   useEffect(() => {
      setTotalItems(filteredOOPs.length);
   }, [filteredOOPs]);

   const descriptions = useMemo(() => ({
      'Payment Proof': 'Order of Payments with pending payment verification',
      'Awaiting Payment': 'Order of Payments awaiting applicant payment',
      'Manual Payment': 'Order of Payments requiring manual payment',
      'Completed Payments': 'Verified payments',
      'Issued OR': 'Order of Payments with issued receipts'
   }), []);

   const descriptionText = useTypewriter(descriptions[activeMainTab] || '');

   const renderTabDescription = () => (
      <div className="mb-4 -mt-4">
         <h1 className="text-sm min-h-[20px] text-black dark:text-gray-300">{descriptionText}</h1>
      </div>
   );

   const renderContent = () => {
      if (oopsLoading) {
         return <div className="flex justify-center py-8">Loading order of payments...</div>;
      }

      if (oopsError) {
         return <div className="text-destructive text-center py-8">Error loading order of payments</div>;
      }

      if (filteredOOPs.length === 0) {
         return (
            <div className="text-center py-8">
               <FileX className="mx-auto h-12 w-12 text-muted-foreground" />
               <h3 className="mt-2 text-lg font-semibold">No orders of payment found</h3>
               <p className="text-sm text-muted-foreground">
                  {filters.applicationType ?
                     `No orders of payment found for ${filters.applicationType}` :
                     'No orders of payment available'}
               </p>
            </div>
         );
      }

      return (
         <Table>
            <TableHeader>
               <TableRow>
                  <TableHead>Application Number</TableHead>
                  <TableHead>Bill Number</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
               </TableRow>
            </TableHeader>
            <TableBody>
               {filteredOOPs
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((oop) => (
                     <BillCollectorOOPRow
                        key={oop._id}
                        oop={oop}
                        onReviewComplete={handleRefetch}
                        currentTab={activeMainTab}
                        isMobile={false}
                     />
                  ))}
            </TableBody>
         </Table>
      );
   };

   const isMobile = useMediaQuery('(max-width: 640px)');

   const { startTour: initiateTour } = useTourGuide({
      tourId: 'bill-collector-tour',
      onFinish: () => {
         console.log('Tour finished');
      },
   });

   const billCollectorTourSteps = useMemo(() => {
      // Base steps that are always available
      const baseSteps = [
         {
            target: '.main-tabs',
            content: 'Switch between different payment stages using these tabs',
            placement: 'bottom',
            disableBeacon: true,
         },
         {
            target: '.applications-filters',
            content: 'Filter order of payments by type, amount, and date range',
            placement: 'bottom',
         }
      ];

      // Add table steps based on whether we have data or not
      if (filteredOOPs?.length > 0) {
         baseSteps.push(
            {
               target: '.applications-table',
               content: 'View all order of payments that need your attention',
               placement: 'top',
            },
            {
               target: '.table-actions',
               content: 'Here you can find different actions for each order of payment',
               placement: 'left',
            },
            {
               target: '.view-oop-action',
               content: 'Click here to view the complete details of the order of payment',
               placement: 'left',
            },
            {
               target: '.print-oop-action',
               content: 'Click here to print the order of payment',
               placement: 'left',
            }
         );

         // Add specific steps based on the current tab
         if (activeMainTab === 'Manual Payment') {
            baseSteps.push({
               target: '.confirm-payment-action',
               content: 'Click here to confirm manual payment',
               placement: 'left',
            });
         } else if (activeMainTab === 'Payment Proof') {
            baseSteps.push({
               target: '.review-payment-action',
               content: 'Click here to review payment proof submitted by the applicant',
               placement: 'left',
            });
         } else if (activeMainTab === 'Completed Payments') {
            baseSteps.push({
               target: '.generate-or-action',
               content: 'Click here to generate Official Receipt',
               placement: 'left',
            });
         }
      } else {
         baseSteps.push({
            target: '.applications-table',
            content: 'Order of payments will appear here once they need your attention',
            placement: 'top',
         });
      }

      return baseSteps;
   }, [activeMainTab, filteredOOPs?.length]);

   // Add useEffect for tour initialization
   useEffect(() => {
      const tourButton = document.querySelector('.help-tour-button');
      if (tourButton) {
         tourButton.disabled = oopsLoading;
      }
   }, [oopsLoading]);

   const handleStartTour = useCallback(async () => {
      // Wait for data to be loaded
      if (oopsLoading) {
         toast.info('Please wait while data is loading...');
         return;
      }

      // Start the tour
      initiateTour(billCollectorTourSteps);
   }, [oopsLoading, initiateTour, billCollectorTourSteps]);

   return (
      <DashboardLayout
         title="Bill Collector Dashboard"
         description="Manage and process order of payments"
         onRefresh={handleRefetch}
         isMobile={isMobile}
         mainTabs={mainTabs}
         activeMainTab={activeMainTab}
         onMainTabChange={handleTabChange}
         tabDescription={renderTabDescription()}
         filters={<OOPFilters filters={filters} setFilters={setFilters} />}
         helpTour={handleStartTour}
         currentPage={currentPage}
         totalItems={totalItems}
         itemsPerPage={itemsPerPage}
         onPageChange={setCurrentPage}
         onItemsPerPageChange={setItemsPerPage}
      >
         {renderContent()}
      </DashboardLayout>
   );
};

export default BillCollectorDashboard;
